import { Component, Inject, OnInit } from "@angular/core";
import { IIndicator } from "@gtmhub/error-handling";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { FeatureToggleService } from "@gtmhub/featureToggle";
import { Ng1StateProxy } from "@gtmhub/core/routing";
import { TagService, ITag } from "@gtmhub/tags";
import { localize } from "@gtmhub/localization";
import { isTagExistsResponseError } from "@gtmhub/tags/utils";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { FeatureFlag } from "@gtmhub/featureToggle/models";

@Component({
  selector: "tag-form",
  templateUrl: "./tag-form.component.html",
  styleUrls: ["./tag-form.component.less"],
})
export class TagFormComponent implements OnInit {
  public initialized = false;
  public indicators: {
    loading?: IIndicator;
  } = {};
  public hasPermission: boolean;
  public tagForm!: FormGroup;
  public inactiveTag?: ITag;

  constructor(
    private ng1StateProxy: Ng1StateProxy,
    private featureToggleService: FeatureToggleService,
    private tagService: TagService,
    private formBuilder: FormBuilder,
    @Inject("$rootScope") private rootScope: IGtmhubRootScopeService
  ) {}

  public ngOnInit(): Promise<void> {
    this.hasPermission = this.rootScope.hasPermission("CreateTags");
    if (!this.hasPermission) {
      return;
    }

    this.tagForm = this.formBuilder.group({
      title: [null, Validators.required],
    });

    return this.featureToggleService.isFeatureAvailable(FeatureFlag.EnableTagManagement).then(
      (featureAvailable) => {
        this.hasPermission = featureAvailable;
        this.initialized = true;
      },
      (error) => {
        this.indicators.loading = { error };
      }
    );
  }

  public save(): Promise<void> {
    if (!this.tagForm.valid) {
      return;
    }

    this.indicators.loading = { progress: true };

    return this.tagService.createTag(this.tagForm.value).then(
      () => {
        this.ng1StateProxy.go("gtmhub.configuration.tags");
      },
      (error) => {
        if (!isTagExistsResponseError(error)) {
          this.indicators.loading = { error: { title: localize(error) } };
          return;
        }

        if (!error.data.data.isActive) {
          this.inactiveTag = error.data.data;
        }

        this.tagForm.setErrors({ tagExists: true });
        this.resetIndicator("loading");
      }
    );
  }

  public activateInactiveTag(): Promise<void> {
    if (!this.inactiveTag?.id) {
      return;
    }

    const tagsIds = [this.inactiveTag.id];
    this.indicators.loading = { progress: true };
    this.clearInactiveTag();
    return this.tagService.changeTagsActivationStatus(tagsIds, "activate").then(
      () => {
        this.resetIndicator("loading");
        this.cancel();
      },
      (error) => {
        this.indicators.loading = { error };
      }
    );
  }

  public clearInactiveTag(): void {
    delete this.inactiveTag;
  }

  public resetIndicator(indicator: keyof TagFormComponent["indicators"]): void {
    delete this.indicators[indicator];
  }

  public cancel(): void {
    this.ng1StateProxy.go("gtmhub.configuration.tags");
  }
}
